import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Tabs, Tab, Card, CardContent, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Dashboard, AccountTree, VpnKey, Delete, Add, Refresh, Group, Edit } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem('admin_auth') === 'true');
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddUsernameDialog, setOpenAddUsernameDialog] = useState(false);
  const [openGroupDialog, setOpenGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [filterGroup, setFilterGroup] = useState('ALL');
  const [filterActive, setFilterActive] = useState('ALL');
  const [newMapping, setNewMapping] = useState({
    azure_email: '',
    rebelnet_username: '',
    azure_group_id: 'MANUALE'
  });
  const [newUsername, setNewUsername] = useState('');
  const [newUsernameGroupId, setNewUsernameGroupId] = useState('MANUALE');
  const [newGroup, setNewGroup] = useState({
    azure_group_id: '',
    azure_group_name: '',
    username_prefix: '',
    userGroupId: '1',
    siteId: 'PADOVA'
  });

  useEffect(() => {
    loadStats();
    loadMappings();
    loadClients();
    loadGroups();
  }, []);

  useEffect(() => {
    let filtered = mappings;

    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(u => 
        u.azure_email?.toLowerCase().includes(search) ||
        u.azure_name?.toLowerCase().includes(search) ||
        u.usernames?.some(un => un.rebelnet_username?.toLowerCase().includes(search))
      );
    }

    if (filterGroup !== 'ALL') {
      filtered = filtered.filter(u => 
        u.usernames.some(un => un.azure_group_id === filterGroup)
      );
    }

    if (filterActive !== 'ALL') {
      const isActive = filterActive === 'ACTIVE';
      filtered = filtered.filter(u => 
        u.usernames.some(un => un.active === isActive)
      );
    }

    setFilteredMappings(filtered);
  }, [searchText, filterGroup, filterActive, mappings]);

  const loadStats = () => {
    fetch(`${API_URL}/admin/api/stats`)
      .then(res => res.json())
      .then(data => setStats(data));
  };

  const loadMappings = () => {
    fetch(`${API_URL}/admin/api/mappings`)
      .then(res => res.json())
      .then(data => {
        setMappings(data.mappings || []);
        setFilteredMappings(data.mappings || []);
      });
  };

  const loadClients = () => {
    fetch(`${API_URL}/admin/api/token-keys`)
      .then(res => res.json())
      .then(data => setClients(data.clients || []));
  };

  const loadGroups = () => {
    fetch(`${API_URL}/admin/api/group-configs`)
      .then(res => res.json())
      .then(data => setGroups(data.groups || []));
  };

  const deleteMapping = (azure_user_id, rebelnet_username) => {
    if (!window.confirm('Eliminare questo mapping?')) return;
    
    fetch(`${API_URL}/admin/api/mappings?azure_user_id=${azure_user_id}&rebelnet_username=${rebelnet_username}`, {
      method: 'DELETE'
    })
    .then(() => loadMappings());
  };

  const refreshAzureId = (azure_email, azure_user_id, rebelnet_username) => {
    fetch(`${API_URL}/admin/api/mappings/refresh-id`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ azure_email: azure_email || null, old_azure_user_id: azure_user_id, rebelnet_username })
    })
    .then(res => res.json())
    .then(() => loadMappings());
  };

  const createMapping = () => {
    fetch(`${API_URL}/admin/api/mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newMapping)
    })
    .then(res => res.json())
    .then(() => {
      setOpenDialog(false);
      setNewMapping({ azure_email: '', rebelnet_username: '', azure_group_id: 'MANUALE' });
      loadMappings();
    });
  };

  const toggleActive = (azure_user_id, rebelnet_username, currentActive) => {
    fetch(`${API_URL}/admin/api/mappings`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ azure_user_id, rebelnet_username, active: !currentActive })
    })
    .then(() => loadMappings());
  };

  const addUsername = () => {
    fetch(`${API_URL}/admin/api/mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        azure_email: selectedUser.azure_email,
        rebelnet_username: newUsername,
        azure_group_id: newUsernameGroupId
      })
    })
    .then(() => {
      setOpenAddUsernameDialog(false);
      setNewUsername('');
      setNewUsernameGroupId('MANUALE');
      setSelectedUser(null);
      loadMappings();
    });
  };

  const createGroup = () => {
    const url = editingGroup 
      ? `${API_URL}/admin/api/group-configs?azure_group_id=${encodeURIComponent(editingGroup.azure_group_id)}`
      : `${API_URL}/admin/api/group-configs`;
    
    const method = editingGroup ? 'PATCH' : 'POST';
    
    fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newGroup)
    })
    .then(() => {
      setOpenGroupDialog(false);
      setEditingGroup(null);
      setNewGroup({ azure_group_id: '', azure_group_name: '', username_prefix: '', userGroupId: '1', siteId: 'PADOVA' });
      loadGroups();
    });
  };

  const deleteGroup = (azure_group_id) => {
    if (!window.confirm('Eliminare questo gruppo?')) return;
    fetch(`${API_URL}/admin/api/group-configs?azure_group_id=${encodeURIComponent(azure_group_id)}`, { method: 'DELETE' })
      .then(res => res.json())
      .then(() => {
        loadGroups();
        loadMappings();
      })
      .catch(err => console.error('Errore eliminazione gruppo:', err));
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'WeCan2024!Admin') {
      sessionStorage.setItem('admin_auth', 'true');
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('Password errata');
    }
  };

  if (!isAuthenticated) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#3F47F5'
      }}>
        <Card sx={{ 
          maxWidth: 400, 
          width: '100%', 
          m: 2,
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: '#3F47F5' }}>
                Admin Panel
              </Typography>
              <Typography variant="body2" color="text.secondary" fontWeight="400">
                Inserisci la password per accedere
              </Typography>
            </Box>
            <form onSubmit={handleLogin}>
              <TextField
                type="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                autoFocus
                sx={{ mb: 3 }}
              />
              <Button 
                type="submit" 
                variant="contained" 
                fullWidth 
                size="large"
                sx={{ 
                  background: '#3F47F5',
                  '&:hover': { background: '#2F37D5' },
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5
                }}
              >
                Accedi
              </Button>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        WeCan Auth 2.0 - Pannello Amministrazione
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<Dashboard />} label="Dashboard" />
          <Tab icon={<AccountTree />} label={`Mappature (${mappings.length})`} />
          <Tab icon={<Group />} label={`Gruppi (${groups.length})`} />
          <Tab icon={<VpnKey />} label={`Client (${clients.length})`} />
        </Tabs>
      </Box>

      {activeTab === 0 && stats && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Mappature Totali</Typography>
                <Typography variant="h3">{stats.total_mappings}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>Token Attivi</Typography>
                <Typography variant="h3">{stats.active_tokens}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Cerca per email, nome o username..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 250 }}
            />
            <TextField
              select
              label="Gruppo"
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              SelectProps={{ native: true }}
            >
              <option value="ALL">Tutti i gruppi</option>
              <option value="MANUALE">MANUALE</option>
              {groups.map(g => (
                <option key={g.azure_group_id} value={g.azure_group_id}>
                  {g.azure_group_name}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Stato"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              size="small"
              sx={{ minWidth: 150 }}
              SelectProps={{ native: true }}
            >
              <option value="ALL">Tutti</option>
              <option value="ACTIVE">Attivi</option>
              <option value="INACTIVE">Disattivi</option>
            </TextField>
            <Button variant="contained" startIcon={<Add />} onClick={() => setOpenDialog(true)}>
              Nuova Mappatura
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Mostrando {filteredMappings.length} di {mappings.length} mappature
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email Azure</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Username Rebel Net</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMappings.map(user => (
                  <TableRow key={user.azure_user_id}>
                    <TableCell>
                      <Typography variant="body2">{user.azure_email}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {user.azure_user_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{user.azure_name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.usernames.map(u => {
                          const groupName = u.azure_group_id === 'MANUALE' 
                            ? 'MANUALE' 
                            : groups.find(g => g.azure_group_id === u.azure_group_id)?.azure_group_name || u.azure_group_id;
                          
                          return (
                            <Box key={u.rebelnet_username} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                              <Chip
                                label={u.rebelnet_username}
                                size="small"
                                color={u.active ? 'primary' : 'default'}
                                onClick={() => toggleActive(user.azure_user_id, u.rebelnet_username, u.active)}
                                onDelete={() => deleteMapping(user.azure_user_id, u.rebelnet_username)}
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', ml: 1 }}>
                                {groupName}
                              </Typography>
                            </Box>
                          );
                        })}
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedUser(user);
                            setOpenAddUsernameDialog(true);
                          }}
                          title="Aggiungi username"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="info"
                        onClick={() => refreshAzureId(user.azure_email, user.azure_user_id, user.usernames[0]?.rebelnet_username)}
                        title="Aggiorna dati Azure"
                      >
                        <Refresh />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => {
                          if (window.confirm('Eliminare tutti i mapping per questo utente?')) {
                            fetch(`${API_URL}/admin/api/mappings?azure_user_id=${user.azure_user_id}`, { method: 'DELETE' })
                              .then(() => loadMappings());
                          }
                        }}
                        title="Elimina utente"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setEditingGroup(null);
              setNewGroup({ azure_group_id: '', azure_group_name: '', username_prefix: '', userGroupId: '1', siteId: 'PADOVA' });
              setOpenGroupDialog(true);
            }}>
              Nuovo Gruppo
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome Gruppo</TableCell>
                  <TableCell>Prefisso Username</TableCell>
                  <TableCell>User Group ID</TableCell>
                  <TableCell>Site ID</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map(g => (
                  <TableRow key={g.azure_group_id}>
                    <TableCell>
                      <Typography variant="body2">{g.azure_group_name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        {g.azure_group_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={g.username_prefix.toUpperCase()} color="primary" size="small" />
                    </TableCell>
                    <TableCell>{g.userGroupId}</TableCell>
                    <TableCell>{g.siteId}</TableCell>
                    <TableCell align="right">
                      <IconButton 
                        color="primary" 
                        onClick={() => {
                          setEditingGroup(g);
                          setNewGroup({
                            azure_group_id: g.azure_group_id,
                            azure_group_name: g.azure_group_name,
                            username_prefix: g.username_prefix,
                            userGroupId: String(g.userGroupId),
                            siteId: g.siteId
                          });
                          setOpenGroupDialog(true);
                        }}
                        title="Modifica gruppo"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteGroup(g.azure_group_id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {activeTab === 3 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Referer</TableCell>
                <TableCell>URL Callback</TableCell>
                <TableCell>Abilitato</TableCell>
                <TableCell>API Rebel Net</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.referer}>
                  <TableCell>{c.referer}</TableCell>
                  <TableCell>{c.callback_url}</TableCell>
                  <TableCell>
                    <Chip label={c.enabled ? 'Sì' : 'No'} color={c.enabled ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip label={c.needs_rebelnet_api ? 'Sì' : 'No'} color={c.needs_rebelnet_api ? 'primary' : 'default'} size="small" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuova Mappatura</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Email Azure"
              value={newMapping.azure_email}
              onChange={(e) => setNewMapping({...newMapping, azure_email: e.target.value})}
              fullWidth
              required
              helperText="L'Azure User ID verrà recuperato automaticamente"
            />
            <TextField
              label="Username Rebel Net"
              value={newMapping.rebelnet_username}
              onChange={(e) => setNewMapping({...newMapping, rebelnet_username: e.target.value})}
              fullWidth
              required
            />
            <TextField
              select
              label="Gruppo Azure"
              value={newMapping.azure_group_id}
              onChange={(e) => setNewMapping({...newMapping, azure_group_id: e.target.value})}
              fullWidth
              required
              SelectProps={{ native: true }}
              helperText="Seleziona il gruppo Azure associato"
            >
              <option value="MANUALE">MANUALE (nessun gruppo)</option>
              {groups.map(g => (
                <option key={g.azure_group_id} value={g.azure_group_id}>
                  {g.azure_group_name}
                </option>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annulla</Button>
          <Button onClick={createMapping} variant="contained">Crea</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddUsernameDialog} onClose={() => setOpenAddUsernameDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Aggiungi Username</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Utente: {selectedUser?.azure_email}
            </Typography>
            <TextField
              label="Username Rebel Net"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              fullWidth
              required
            />
            <TextField
              select
              label="Gruppo Azure"
              value={newUsernameGroupId}
              onChange={(e) => setNewUsernameGroupId(e.target.value)}
              fullWidth
              required
              SelectProps={{ native: true }}
              helperText="Seleziona il gruppo Azure associato"
            >
              <option value="MANUALE">MANUALE (nessun gruppo)</option>
              {groups.map(g => (
                <option key={g.azure_group_id} value={g.azure_group_id}>
                  {g.azure_group_name}
                </option>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddUsernameDialog(false)}>Annulla</Button>
          <Button onClick={addUsername} variant="contained">Aggiungi</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openGroupDialog} onClose={() => {
        setOpenGroupDialog(false);
        setEditingGroup(null);
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingGroup ? 'Modifica Gruppo Azure' : 'Nuovo Gruppo Azure'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Azure Group ID"
              value={newGroup.azure_group_id}
              onChange={(e) => setNewGroup({...newGroup, azure_group_id: e.target.value})}
              fullWidth
              required
              disabled={!!editingGroup}
              helperText="UUID del gruppo Azure AD"
            />
            <TextField
              label="Nome Gruppo"
              value={newGroup.azure_group_name}
              onChange={(e) => setNewGroup({...newGroup, azure_group_name: e.target.value})}
              fullWidth
              required
            />
            <TextField
              label="Prefisso Username"
              value={newGroup.username_prefix}
              onChange={(e) => setNewGroup({...newGroup, username_prefix: e.target.value.toLowerCase()})}
              fullWidth
              required
              helperText="Es: eni → ENI_nome_cognome (max 20 caratteri)"
            />
            <TextField
              label="User Group ID"
              type="number"
              value={newGroup.userGroupId}
              onChange={(e) => setNewGroup({...newGroup, userGroupId: e.target.value})}
              fullWidth
              required
            />
            <TextField
              label="Site ID"
              value={newGroup.siteId}
              onChange={(e) => setNewGroup({...newGroup, siteId: e.target.value})}
              fullWidth
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenGroupDialog(false);
            setEditingGroup(null);
          }}>Annulla</Button>
          <Button onClick={createGroup} variant="contained">{editingGroup ? 'Salva' : 'Crea'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AdminDashboard;
